import React, { useCallback, useEffect, useState } from 'react'
import { Bot, Loader2, AlertCircle, Trash2, Pencil, Plus, X, Eye, EyeOff } from 'lucide-react'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import {
  listAssistantTopicsAdmin,
  getAssistantStats,
  createAssistantTopic,
  updateAssistantTopic,
  changeAssistantTopicStatus,
  deleteAssistantTopic,
  ASSISTANT_CATEGORY_KEYS,
  ASSISTANT_CATEGORY_LABELS,
  ASSISTANT_STATUS_LABELS,
  type AssistantTopic,
  type AssistantTopicRequest,
  type AssistantTopicCategory,
  type ResourceStatus,
} from '@/services/assistantService'

const STATUS_FILTERS: (ResourceStatus | 'ALL')[] = ['ALL', 'DRAFT', 'PUBLISHED', 'ARCHIVED']
const STATUS_LIST: ResourceStatus[] = ['DRAFT', 'PUBLISHED', 'ARCHIVED']

const statusTone: Record<ResourceStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  PUBLISHED: 'bg-green-100 text-green-700',
  ARCHIVED: 'bg-amber-100 text-amber-700',
}

const EMPTY_FORM: AssistantTopicRequest = {
  title: '', slug: '', answer: '', triggerPhrases: '', keywords: '',
  category: 'GENERAL_INFO', officialSourceLabel: '', officialSourceUrl: '',
  portalLinkLabel: '', portalLinkPath: '', sortOrder: 0, status: 'DRAFT',
}

const AssistantManagement = React.memo(() => {
  const [items, setItems] = useState<AssistantTopic[]>([])
  const [stats, setStats] = useState<{ TOTAL: number; DRAFT: number; PUBLISHED: number; ARCHIVED: number }>({ TOTAL: 0, DRAFT: 0, PUBLISHED: 0, ARCHIVED: 0 })
  const [statusFilter, setStatusFilter] = useState<ResourceStatus | 'ALL'>('ALL')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actingId, setActingId] = useState<number | null>(null)
  const [toast, setToast] = useState<{ tone: 'success' | 'error'; msg: string } | null>(null)
  const [editing, setEditing] = useState<AssistantTopic | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<AssistantTopicRequest>(EMPTY_FORM)
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const size = 20

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const [data, s] = await Promise.all([
        listAssistantTopicsAdmin({ status: statusFilter === 'ALL' ? undefined : statusFilter, q: q || undefined, page, size }),
        getAssistantStats(),
      ])
      setItems(data.content); setTotalPages(data.totalPages); setStats(s)
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load assistant topics.')
    } finally { setLoading(false) }
  }, [statusFilter, q, page])

  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(0) }, [statusFilter, q])

  const flash = (tone: 'success' | 'error', msg: string) => { setToast({ tone, msg }); setTimeout(() => setToast(null), 3000) }

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setFormError(null); setShowForm(true) }
  const openEdit = (tp: AssistantTopic) => {
    setEditing(tp)
    setForm({
      title: tp.title, slug: tp.slug, answer: tp.answer,
      triggerPhrases: tp.triggerPhrases ?? '', keywords: tp.keywords ?? '',
      category: tp.category, officialSourceLabel: tp.officialSourceLabel ?? '',
      officialSourceUrl: tp.officialSourceUrl ?? '', portalLinkLabel: tp.portalLinkLabel ?? '',
      portalLinkPath: tp.portalLinkPath ?? '', sortOrder: tp.sortOrder ?? 0, status: tp.status ?? 'DRAFT',
    })
    setFormError(null); setShowForm(true)
  }
  const closeForm = () => { setShowForm(false); setEditing(null); setForm(EMPTY_FORM); setFormError(null) }

  const save = async (ev: React.FormEvent) => {
    ev.preventDefault()
    if (!form.title.trim()) { setFormError('Title is required.'); return }
    if (!form.answer.trim()) { setFormError('Answer is required.'); return }
    if (!form.category) { setFormError('Category is required.'); return }
    setSaving(true); setFormError(null)
    try {
      const payload: AssistantTopicRequest = { ...form, title: form.title.trim(), answer: form.answer.trim() }
      if (editing) {
        await updateAssistantTopic(editing.id, payload)
        flash('success', `Updated “${form.title}”`)
      } else {
        await createAssistantTopic(payload)
        flash('success', `Created “${form.title}”`)
      }
      closeForm(); await load()
    } catch (e: any) {
      setFormError(e?.response?.data?.message || 'Failed to save.')
    } finally { setSaving(false) }
  }

  const changeStatus = async (tp: AssistantTopic, status: ResourceStatus) => {
    setActingId(tp.id)
    try { await changeAssistantTopicStatus(tp.id, status); flash('success', `“${tp.title}” → ${ASSISTANT_STATUS_LABELS[status]}`); await load() }
    catch (e: any) { flash('error', e?.response?.data?.message || 'Failed.') }
    finally { setActingId(null) }
  }

  const remove = async (tp: AssistantTopic) => {
    if (!window.confirm(`Delete “${tp.title}”? This cannot be undone.`)) return
    setActingId(tp.id)
    try { await deleteAssistantTopic(tp.id); flash('success', 'Topic deleted'); await load() }
    catch (e: any) { flash('error', e?.response?.data?.message || 'Failed.') }
    finally { setActingId(null) }
  }

  return (
    <GovLayout showSidebar={false}>
      <div className="space-y-6">
        <GovPageTitle title="TB Assistant" subtitle="Curate the controlled assistant's knowledge base — only link to verified official Central TB Division / Ni-Kshay sources. Do not fabricate medical data." icon={Bot} />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <GovCard className="text-center"><p className="text-2xl font-bold text-gray-900">{stats.TOTAL}</p><p className="text-sm text-gray-500">Total</p></GovCard>
          <GovCard className="text-center"><p className="text-2xl font-bold text-gray-500">{stats.DRAFT}</p><p className="text-sm text-gray-500">Draft</p></GovCard>
          <GovCard className="text-center"><p className="text-2xl font-bold text-green-600">{stats.PUBLISHED}</p><p className="text-sm text-gray-500">Published</p></GovCard>
          <GovCard className="text-center"><p className="text-2xl font-bold text-amber-600">{stats.ARCHIVED}</p><p className="text-sm text-gray-500">Archived</p></GovCard>
        </div>

        <GovCard>
          <div className="flex flex-wrap items-center gap-2">
            {STATUS_FILTERS.map((f) => (
              <button key={f} onClick={() => setStatusFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium ${statusFilter === f ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                {f === 'ALL' ? 'All' : ASSISTANT_STATUS_LABELS[f]}
              </button>
            ))}
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title/answer…" className="text-sm border border-gray-200 rounded-md px-3 py-1.5 ml-auto" />
            <GovButton variant="primary" size="sm" onClick={openCreate} icon={<Plus className="w-4 h-4" />}>Add topic</GovButton>
          </div>
        </GovCard>

        {toast && (
          <div role="status" className={`flex items-center gap-2 p-3 rounded-lg text-sm ${toast.tone === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {toast.tone === 'success' ? '✓' : <AlertCircle className="w-4 h-4" />} {toast.msg}
          </div>
        )}

        <GovCard>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-gray-900">Assistant topics</h2>
            <button onClick={load} disabled={loading} className="text-xs text-blue-600 hover:underline disabled:opacity-50">{loading ? 'Loading…' : 'Refresh'}</button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
          ) : error ? (
            <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg text-sm"><AlertCircle className="w-4 h-4" /> {error}</div>
          ) : items.length === 0 ? (
            <GovEmptyState icon="🤖" title="No topics" description="Add your first assistant topic with a verified official source, or adjust the filter/search." />
          ) : (
            <div className="divide-y divide-gray-100">
              {items.map((tp) => (
                <div key={tp.id} className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${statusTone[tp.status ?? 'DRAFT']}`}>{ASSISTANT_STATUS_LABELS[tp.status ?? 'DRAFT']}</span>
                        <span className="text-xs text-gray-400">{ASSISTANT_CATEGORY_LABELS[tp.category]}</span>
                        {tp.sortOrder != null && <span className="text-xs text-gray-300">#{tp.sortOrder}</span>}
                      </div>
                      <h4 className="font-medium text-gray-900">{tp.title}</h4>
                      <p className="text-sm text-gray-600 line-clamp-2 mt-1">{tp.answer}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {tp.officialSourceLabel && `${tp.officialSourceLabel}`}
                        {tp.portalLinkPath && ` · → ${tp.portalLinkPath}`}
                      </p>
                      {tp.officialSourceUrl && (
                        <a href={tp.officialSourceUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline mt-1 inline-block truncate max-w-full">
                          {tp.officialSourceUrl}
                        </a>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                      {actingId === tp.id && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                      {tp.status === 'PUBLISHED'
                        ? <GovButton variant="ghost" size="sm" onClick={() => changeStatus(tp, 'ARCHIVED')} icon={<EyeOff className="w-3.5 h-3.5" />}>Archive</GovButton>
                        : <GovButton variant="ghost" size="sm" onClick={() => changeStatus(tp, 'PUBLISHED')} icon={<Eye className="w-3.5 h-3.5" />}>Publish</GovButton>}
                      <button onClick={() => openEdit(tp)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(tp)} disabled={actingId === tp.id} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>Previous</GovButton>
              <span className="text-xs text-gray-500">Page {page + 1} of {totalPages}</span>
              <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>Next</GovButton>
            </div>
          )}
        </GovCard>

        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" onClick={closeForm}>
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{editing ? 'Edit topic' : 'Add topic'}</h3>
                  <p className="text-sm text-gray-500 mt-1">Write a factual, agenda-scoped answer that references a verified official source (Central TB Division / Ni-Kshay). Do not fabricate medical statistics or give personal medical advice.</p>
                </div>
                <button onClick={closeForm} className="text-gray-400 hover:text-gray-700" aria-label="Close"><X className="w-5 h-5" /></button>
              </div>

              {formError && <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 text-red-800 rounded-lg text-sm" role="alert"><AlertCircle className="w-4 h-4" /> {formError}</div>}

              <form onSubmit={save} noValidate className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="a-title">Title <span className="text-red-500">*</span></label>
                  <input id="a-title" type="text" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="a-slug">Slug (optional)</label>
                    <input id="a-slug" type="text" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="input" placeholder="auto from title" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="a-cat">Category <span className="text-red-500">*</span></label>
                    <select id="a-cat" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as AssistantTopicCategory })} className="select">
                      {ASSISTANT_CATEGORY_KEYS.map((c) => <option key={c} value={c}>{ASSISTANT_CATEGORY_LABELS[c]}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="a-answer">Answer <span className="text-red-500">*</span></label>
                  <textarea id="a-answer" rows={5} required value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} className="input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="a-trig">Trigger phrases (one per line)</label>
                  <textarea id="a-trig" rows={3} value={form.triggerPhrases} onChange={(e) => setForm({ ...form, triggerPhrases: e.target.value })} className="input" placeholder={"what are the symptoms of tb\ncough for 2 weeks\nsigns of tuberculosis"} />
                  <p className="text-xs text-gray-400 mt-1">Natural phrases a user might type — used for matching.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="a-kw">Keywords (comma-separated)</label>
                  <input id="a-kw" type="text" value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} className="input" placeholder="symptoms, cough, fever, night sweats" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="a-oslabel">Official source label</label>
                    <input id="a-oslabel" type="text" value={form.officialSourceLabel} onChange={(e) => setForm({ ...form, officialSourceLabel: e.target.value })} className="input" placeholder="e.g. Central TB Division, MoHFW" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="a-osurl">Official source URL</label>
                    <input id="a-osurl" type="url" value={form.officialSourceUrl} onChange={(e) => setForm({ ...form, officialSourceUrl: e.target.value })} className="input" placeholder="https://tbcindia.mohfw.gov.in/" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="a-pllabel">Portal link label</label>
                    <input id="a-pllabel" type="text" value={form.portalLinkLabel} onChange={(e) => setForm({ ...form, portalLinkLabel: e.target.value })} className="input" placeholder="e.g. Find a TB centre" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="a-plpath">Portal link path</label>
                    <input id="a-plpath" type="text" value={form.portalLinkPath} onChange={(e) => setForm({ ...form, portalLinkPath: e.target.value })} className="input" placeholder="e.g. /find-tb-centre" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="a-sort">Sort order</label>
                    <input id="a-sort" type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })} className="input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="a-status">Status</label>
                    <select id="a-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as ResourceStatus })} className="select">
                      {STATUS_LIST.map((s) => <option key={s} value={s}>{ASSISTANT_STATUS_LABELS[s]}</option>)}
                    </select>
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <GovButton type="submit" variant="primary" disabled={saving} icon={saving ? <Loader2 className="w-4 h-4 animate-spin" /> : undefined}>{saving ? 'Saving…' : 'Save topic'}</GovButton>
                  <GovButton variant="ghost" onClick={closeForm}>Cancel</GovButton>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </GovLayout>
  )
})

AssistantManagement.displayName = 'AssistantManagement'
export default AssistantManagement